import { useMemo, useEffect, useState } from "react";
import { Chip } from "@remo-co/ui-core/src/components/Chip";
import { Divider } from "@remo-co/ui-core/src/components/Divider";
import { FormHelperText } from "@remo-co/ui-core/src/components/FormHelperText";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { useHistory } from "react-router";
import { useI18n } from "i18n";
import { format } from "helpers/time/format";
import { useCompanyPlanSettings } from "modules/companyPlanSettings";
import { getCurrencySymbol } from "modules/settings/utils";
import { ISubscriptionDetail } from "types/subscription";
import { IHistoryLocation } from "types/history";
import { useNeedHelp } from "modules/helpChat";
import { isTrialCompleted } from "modules/companyPlanSettings/utils/trial";
import { useAppDispatch } from "store/hooks";
import {
  addDialogNotification,
  closeDialogNotification,
} from "modules/dialogNotification/redux/dialogNotificationSlice";
import { Box } from "@remo-co/ui-core/src/components/Box";
import { styles } from "./planDetailsStyles";

export interface IPlanDetailsProps {
  subscriptionDetail: ISubscriptionDetail | null;
  isLoading: boolean;
}

const PlanDetails = ({ subscriptionDetail, isLoading }: IPlanDetailsProps) => {
  const { t } = useI18n(["common", "settings"]);
  const { location } = useHistory<IHistoryLocation>();
  const [isAfterPayment, setIsAfterPayment] = useState<boolean>(
    location.state && location.state.afterPayment,
  );
  const dispatch = useAppDispatch();
  const { callApi } = useNeedHelp();
  const { myCompanyPlan, isFreePlan, isAppsumoPlan } = useCompanyPlanSettings();

  useEffect(() => {
    if (isAfterPayment && !isLoading) {
      const newPlan =
        subscriptionDetail && subscriptionDetail.plan
          ? subscriptionDetail.plan
          : null;

      if (!newPlan) {
        dispatch(
          addDialogNotification({
            hideCloseButton: true,
            confirmText: t("need.help"),
            title: t("awesome"),
            message: t("settings:plan.upgrade.success"),
            onConfirm: () => {
              dispatch(closeDialogNotification());
              callApi("showNewMessage");
            },
          }),
        );
      }
      setIsAfterPayment(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isAfterPayment]);

  const plan =
    subscriptionDetail && subscriptionDetail.plan
      ? subscriptionDetail.plan
      : null;

  const planType = useMemo(() => {
    if (plan) return plan.name;

    if (isTrialCompleted(myCompanyPlan)) return "Post Trial";

    return "Free Trial";
  }, [myCompanyPlan, plan]);

  if (isLoading) {
    return (
      <>
        <Typography variant="h5">{t("settings:my.plan")}</Typography>
        <div className="block">
          <strong>{t("loading")}</strong>
        </div>
      </>
    );
  }

  const customer =
    subscriptionDetail && subscriptionDetail.customer
      ? subscriptionDetail.customer
      : null;

  const nextBillingAt =
    subscriptionDetail && subscriptionDetail.subscription
      ? subscriptionDetail.subscription.next_billing_at
      : null;
  const nextBillingAtText = nextBillingAt
    ? format(nextBillingAt * 1000, "D MMMM, YYYY")
    : "N/A";
  const nextInvoiceAmount =
    subscriptionDetail && subscriptionDetail.subscription
      ? subscriptionDetail.subscription.next_invoice_amount
      : null;
  const nextInvoiceAmountModifier =
    plan && plan.currency_code === "JPY" ? 1 : 100; // for JPY plans, do not divide by 100
  const nextInvoiceAmountText = nextInvoiceAmount
    ? // Convert to readable number with commas
      `${getCurrencySymbol(plan && plan.currency_code)}${(
        nextInvoiceAmount / nextInvoiceAmountModifier
      ).toLocaleString(navigator.language, { minimumFractionDigits: 0 })}`
    : null;
  const coupons = subscriptionDetail?.subscription?.coupons;
  const addons = subscriptionDetail?.subscription?.addons;
  const dueInvoicesCount = subscriptionDetail?.subscription?.due_invoices_count;
  const dueSince = subscriptionDetail?.subscription?.due_since;
  const isSubscriptionCancelled =
    subscriptionDetail?.subscription?.status === "non_renewing";
  const currentTermEndsAt = subscriptionDetail?.subscription?.current_term_end;
  const currentTermEndsAtText = currentTermEndsAt
    ? format(currentTermEndsAt * 1000, "D MMMM, YYYY")
    : "N/A";

  return (
    <Box sx={styles.container}>
      <Typography variant="h3" sx={styles.title}>
        {t("settings:my.plan")}
      </Typography>
      <Box className="block">
        {plan && (
          <Typography variant="body1" sx={styles.planDetails}>
            {t("settings:billing.email")}{" "}
            <strong data-test-id="billing-email">
              {customer ? customer.email : "NA"}
            </strong>
          </Typography>
        )}
        <Typography variant="body1" sx={styles.planDetails}>
          {t("settings:plan.type")} <strong>{planType}</strong>
        </Typography>
        {plan &&
          !isSubscriptionCancelled &&
          !isFreePlan(myCompanyPlan) &&
          !isAppsumoPlan && (
            <Typography variant="body1" sx={styles.planDetails}>
              {t("settings:next.renewal.date")}{" "}
              <strong data-testid="next-billing-at">{nextBillingAtText}</strong>
            </Typography>
          )}
        {!myCompanyPlan?.isOneTimePlan && isSubscriptionCancelled && (
          <>
            <Divider
              style={{
                marginTop: "10px",
                marginBottom: "10px",
                maxWidth: "25%",
              }}
            />
            <Typography variant="subtitle2">
              {t("settings:subscription.renewal.canceled")}
            </Typography>
            <Typography
              variant="body1"
              style={{ opacity: "70%" }}
              data-testid="current-term-ends"
            >
              {`You will have access to ${planType} until ${currentTermEndsAtText}.`}
            </Typography>
          </>
        )}
        {nextInvoiceAmountText && (
          <Typography variant="body1">
            {t("settings:next.invoice")}
            <strong> {nextInvoiceAmountText}</strong>
          </Typography>
        )}
        {addons && addons.length > 0 && (
          <Box sx={styles.add}>
            <Typography variant="body1" sx={styles.addOn}>
              {t("common:addons")}: &nbsp;
            </Typography>
            {addons.map(({ id: addon_id, name }) => (
              // eslint-disable-next-line react/jsx-no-useless-fragment
              <>
                {name && (
                  <Chip
                    label={name}
                    key={addon_id}
                    size="small"
                    color="gray"
                    sx={styles.addOnChip}
                    data-testid="addons-chip"
                  />
                )}
              </>
            ))}
          </Box>
        )}
        {coupons && (
          <Typography variant="body1">
            {t("settings:applied.coupons")}{" "}
            {coupons.map(({ coupon_id }) => (
              <Chip label={coupon_id} key={coupon_id} color="gray" />
            ))}
          </Typography>
        )}
        {!!dueInvoicesCount && !!dueSince && (
          <FormHelperText
            style={{ color: "#f44336" }}
            data-testid="payment-failed"
          >
            {t("settings:payment.failed")}{" "}
            {format(dueSince * 1000, "MMM. Do, YYYY hh:mma")}
          </FormHelperText>
        )}
      </Box>
    </Box>
  );
};

export default PlanDetails;
